/**
 * Centralized Dynamic Component Exports
 *
 * This file provides reusable dynamic imports for components across the application.
 * Benefits:
 * - Avoids circular import issues
 * - Centralizes dynamic loading configuration
 * - Makes components easily reusable with consistent loading behavior
 * - Enables better code splitting and performance optimization
 * - Provides full TypeScript support with proper prop types
 */

import dynamic from "next/dynamic"

// Type-only imports for better bundle optimization
import type { HoverLinkProps } from "@/ui/components/HoverLinks"
import type { QuoteCarouselProps } from "@/ui/components/QuoteCarousel"

// Component prop types for dynamic imports
type BottomCTABannerProps = {
  eyebrow?: string
  headline: string
  buttonCTA: string
  buttonLink: string
  description: Array<string> | string
  bgClass?: string
  secondaryButtonLink?: string
  secondaryButtonCTA?: string
  leftImage?: React.ReactNode
  rightImage?: React.ReactNode
}

/**
 * Dynamic import for the unified QuoteCarousel component
 * Used across homepage, careers, and customers with different props
 */
export const QuoteCarouselDynamic = dynamic<QuoteCarouselProps>(
  () => import("@/ui/components/QuoteCarousel"),
  {
    loading: () => null,
    ssr: true,
  },
)

/**
 * Dynamic import for the HoverLinks component
 * Fully typed with HoverLinkProps for better IntelliSense
 */
export const HoverLinksDynamic = dynamic<HoverLinkProps>(
  () => import("@/ui/components/HoverLinks"),
  {
    loading: () => null,
    ssr: true,
  },
)

/**
 * Dynamic import for the BottomCTABanner component
 * Includes all prop options for flexible CTA configurations
 */
export const BottomCTABannerDynamic = dynamic<BottomCTABannerProps>(
  () => import("@/ui/components/BottomCTABanner"),
  {
    loading: () => null,
    ssr: true,
  },
)
